"use client";
import dotImage from "@/assets/landingPage/icon/dot-icon.svg";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { useSettings } from "@/context/settingsContext";
import ApiPath from "@/network/api/apiPath";
import NetworkService from "@/network/service/networkService";
import {
  javaCode,
  javaCode2,
  javaCode3,
  javaCode4,
  javaCode5,
  javaCode6,
  javaCode7,
  phpCode,
  phpCode2,
  phpCode3,
  phpCode4,
  phpCode5,
  phpCode6,
  phpCode7,
} from "@/utils/codeConvert";
import { getSettingValue } from "@/utils/utils";
import Image from "next/image";
import { useEffect, useState } from "react";
import CodeBlock from "../shared/CodeBlock";

const ApiDocumentationClient = () => {
  const network = new NetworkService();
  const [activeSection, setActiveSection] = useState("currencies");
  const [allCurrencies, setAllCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const { settings } = useSettings();
  const currencySymbol = getSettingValue(settings, "currency_symbol");
  const currencyCode = getSettingValue(settings, "site_currency");

  const fetchAllCurrencies = async () => {
    setLoading(true);
    try {
      const res = await network.globalGet(ApiPath.allCurrency);
      if (res.status === "completed") {
        setAllCurrencies(res.data.data);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllCurrencies();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "currencies",
        "obtain-api-key",
        "access-token",
        "make-payment",
        "ipn",
      ];
      const sectionElements = sections.map((id) => ({
        id,
        element: document.getElementById(id),
      }));
      let currentSection = sections[0];
      let closestDistance = Infinity;

      for (const { id, element } of sectionElements) {
        if (element) {
          const rect = element.getBoundingClientRect();
          const distance = Math.abs(rect.top - 150);
          if (rect.top <= 150 && distance < closestDistance) {
            currentSection = id;
            closestDistance = distance;
          }
        }
      }
      setActiveSection(currentSection);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const apiUrl = process.env.NEXT_PUBLIC_API_BASE_URL;

  const navigationItems = [
    { id: "currencies", label: "Currencies" },
    { id: "obtain-api-key", label: "Obtain Api Key" },
    { id: "access-token", label: "Access Token" },
    { id: "make-payment", label: "Make Payment" },
    { id: "ipn", label: "IPN" },
  ];

  return (
    <div className="section_space_py api-documentation-section">
      <div className="custom-container mx-auto">
        <div className="grid grid-cols-12 gap-0 md:gap-7.5">
          <div className="col-span-12 md:col-span-9">
            <div className="me-0 xl:me-[50px] 2xl:me-[140px]">
              <div id="currencies" className="mb-10">
                <div className="flex items-center gap-2.5 mb-4">
                  <Image
                    src={dotImage}
                    alt="dot"
                    width={100}
                    height={100}
                    className="w-[10px] h-[10px]"
                  />
                  <h5>Currencies</h5>
                </div>
                <p>Our Supported Currencies are listed below.</p>
                <div className="mt-7.5">
                  <div className="max-w-full overflow-x-auto">
                    <div className="common-table common-table-2 common-table-3 min-w-[800px] border border-[rgba(45,45,45,0.10)] rounded-[10px]">
                      <div className="full-table">
                        <table className="w-full whitespace-nowrap">
                          <thead className="table-head">
                            <tr className="table-head-tr">
                              <th className="table-th table-th-first-home">
                                Currency Name
                              </th>
                              <th className="table-th">Currency Symbol</th>
                              <th className="table-th table-th-last-home">
                                Currency Code
                              </th>
                            </tr>
                          </thead>

                          <tbody className="table-body">
                            {loading ? (
                              <tr>
                                <td colSpan="3" className="text-center py-5">
                                  <div className="flex justify-center items-center">
                                    <LoadingSpinner />
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              <>
                                <tr className="table-body-tr last:!border-0">
                                  <td className="table-td table-td-2">
                                    <span className="text-[13px] text-user-text font-semibold">
                                      Main Wallet
                                    </span>
                                  </td>
                                  <td className="table-td table-td-2">
                                    <span className="text-[13px] text-user-text font-semibold">
                                      {currencySymbol}
                                    </span>
                                  </td>
                                  <td className="table-td table-td-2">
                                    <span className="text-[13px] text-user-text font-semibold">
                                      {currencyCode}
                                    </span>
                                  </td>
                                </tr>
                                {allCurrencies.map((currency, index) => (
                                  <tr
                                    key={index}
                                    className="table-body-tr last:!border-0"
                                  >
                                    <td className="table-td table-td-2">
                                      <span className="text-[13px] text-user-text font-semibold">
                                        {currency.name}
                                      </span>
                                    </td>
                                    <td className="table-td table-td-2">
                                      <span className="text-[13px] text-user-text font-semibold">
                                        {currency.symbol}
                                      </span>
                                    </td>
                                    <td className="table-td table-td-2">
                                      <span className="text-[13px] text-user-text font-semibold">
                                        {currency.code}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div id="obtain-api-key" className="mb-10">
                <div className="flex items-center gap-2.5 mb-4">
                  <Image
                    src={dotImage}
                    alt="dot"
                    width={100}
                    height={100}
                    className="w-[10px] h-[10px]"
                  />
                  <h5>Obtain API Key</h5>
                </div>
                <p className="">
                  Login to your merchant account. <br /> Go to
                  <a href="#">{" Merchant App -> API Access Key"}</a> .
                </p>
                <p>
                  You can find your API keys
                  <a href="#"> Public Key</a> and <a href="#"> Secret Key</a> in{" "}
                  <a href="#"> API Access Key</a> menu from merchant dashboard.
                  These keys are required to call API requests. If needed, you
                  can generate a new API key anytime by clicking the{" "}
                  <a href="#"> Regenerate</a> button. Make sure to keep these
                  keys private and never share them with anyone.
                </p>
              </div>

              <div id="access-token" className="mb-10">
                <div className="flex items-center gap-2.5 mb-4">
                  <Image
                    src={dotImage}
                    alt="dot"
                    width={100}
                    height={100}
                    className="w-[10px] h-[10px]"
                  />
                  <h5 className="text-[18px] sm:text-[20px] font-bold text-user-text">
                    Access Token{" "}
                    <span className="ms-4 h-[30px] bg-[rgba(116,69,255,0.04)] rounded-[10px] px-[20px] inline-flex justify-center items-center text-[14px] font-semibold text-user-primary border border-[rgba(116,69,255,0.30)]">
                      POST
                    </span>
                  </h5>
                </div>
                <p>
                  <a href="#">Access Token</a> is required to make payment
                  requests through the API. It serves as a secure key to
                  authorize and validate your transactions. You can generate the
                  Access Token using your API credentials Public Key and include
                  it in the header of every payment request. Ensure the token is
                  kept secure and updated regularly to maintain the security of
                  your transactions.
                </p>
                <ul>
                  <li className="flex flex-wrap items-center">
                    <span>Live URL:</span>
                    <a href="#">{apiUrl}/merchant/access-token</a>
                  </li>
                  <li className="flex flex-wrap items-center">
                    <span>Test URL:</span>
                    <a href="#">{apiUrl}/merchant/sandbox/access-token</a>
                  </li>
                  <li className="flex flex-wrap items-center">
                    <span>Request Method:</span>
                    <i>POST</i>
                  </li>
                </ul>
                <div className="space-y-7.5">
                  <div>
                    <CodeBlock
                      defaultLang="php"
                      dropdown={true}
                      title="Example PHP code"
                      codes={{
                        php: phpCode,
                        java: javaCode,
                      }}
                    />
                  </div>
                  <div>
                    <CodeBlock
                      defaultLang="php"
                      dropdown={false}
                      title="Example Response"
                      status="success"
                      statusText="Success 200"
                      codes={{
                        php: phpCode2,
                        java: javaCode2,
                      }}
                    />
                  </div>
                  <div>
                    <CodeBlock
                      defaultLang="php"
                      dropdown={false}
                      title="Example Response"
                      status="error"
                      statusText="Error (400/401)"
                      codes={{
                        php: phpCode3,
                        java: javaCode3,
                      }}
                    />
                  </div>
                </div>
              </div>

              <div id="make-payment" className="mb-10">
                <div className="flex items-center gap-2.5 mb-4">
                  <Image
                    src={dotImage}
                    alt="dot"
                    width={100}
                    height={100}
                    className="w-[10px] h-[10px]"
                  />
                  <h5 className="text-[18px] sm:text-[20px] font-bold text-user-text">
                    Make Payment{" "}
                    <span className="ms-4 h-[30px] bg-[rgba(116,69,255,0.04)] rounded-[10px] px-[20px] inline-flex justify-center items-center text-[14px] font-semibold text-user-primary border border-[rgba(116,69,255,0.30)]">
                      POST
                    </span>
                  </h5>
                </div>
                <p>
                  To get started with the payment process, simply follow the
                  example code and ensure the parameters are set correctly. Be
                  sure to use the provided API endpoints for your requests.
                </p>
                <ul>
                  <li className="flex flex-wrap items-center">
                    <span>Live URL:</span>
                    <a href="#">{apiUrl}/merchant/make-payment</a>
                  </li>
                  <li className="flex flex-wrap items-center">
                    <span>Test URL:</span>
                    <a href="#">{apiUrl}/sandbox/make-payment</a>
                  </li>
                  <li className="flex flex-wrap items-center">
                    <span>Request Method:</span>
                    <span className="h-[30px] bg-[rgba(116,69,255,0.04)] rounded-[10px] px-[20px] inline-flex justify-center items-center text-[14px] font-semibold text-user-primary border border-[rgba(116,69,255,0.30)]">
                      POST
                    </span>
                  </li>
                  <li className="flex flex-wrap items-center">
                    <span>UID:</span>
                    <i>12344567890</i>
                  </li>
                  <li className="flex flex-wrap items-center">
                    <span>Password:</span>
                    <i>12345678</i>
                  </li>
                </ul>
                <p>Request to the endpoint with the following parameters:</p>
                <div>
                  <div className="max-w-full overflow-x-auto">
                    <div className="common-table common-table-2 common-table-3 min-w-[800px] border border-[rgba(45,45,45,0.10)] rounded-[10px]">
                      <div className="full-table">
                        <table className="w-full">
                          <thead className="table-head">
                            <tr className="table-head-tr">
                              <th className="table-th table-th-first-home">
                                Param Name
                              </th>
                              <th className="table-th">Param Type</th>
                              <th className="table-th table-th-last-home">
                                Description
                              </th>
                            </tr>
                          </thead>

                          <tbody className="table-body">
                            <tr className="table-body-tr last:!border-0">
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  amount
                                </span>
                              </td>
                              <td className="table-td table-td-2 flex items-center">
                                <span className="text-[13px] text-user-text font-semibold">
                                  float|integer
                                </span>
                                <span className="ms-2 h-[26px] rounded-full px-[14px] inline-flex justify-center items-center text-[14px] font-semibold text-user-error border border-[rgba(220,60,34,0.20)]">
                                  Required
                                </span>
                              </td>
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  The amount to be charged for the transaction.
                                </span>
                              </td>
                            </tr>
                            <tr className="table-body-tr last:!border-0">
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  currency
                                </span>
                              </td>
                              <td className="table-td table-td-2 flex items-center">
                                <span className="text-[13px] text-user-text font-semibold">
                                  string (4)
                                </span>
                                <span className="ms-2 h-[26px] rounded-full px-[14px] inline-flex justify-center items-center text-[14px] font-semibold text-user-error border border-[rgba(220,60,34,0.20)]">
                                  Required
                                </span>
                              </td>
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  Currency Code (e.g., USD, EUR)
                                </span>
                              </td>
                            </tr>
                            <tr className="table-body-tr last:!border-0">
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  transaction_id
                                </span>
                              </td>
                              <td className="table-td table-td-2 flex items-center">
                                <span className="text-[13px] text-user-text font-semibold">
                                  string (12)
                                </span>
                                <span className="ms-2 h-[26px] rounded-full px-[14px] inline-flex justify-center items-center text-[14px] font-semibold text-user-error border border-[rgba(220,60,34,0.20)]">
                                  Required
                                </span>
                              </td>
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  A unique identifier for the transaction,
                                  consisting of 12 alphanumeric characters.
                                </span>
                              </td>
                            </tr>
                            <tr className="table-body-tr last:!border-0">
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  description
                                </span>
                              </td>
                              <td className="table-td table-td-2 flex items-center">
                                <span className="text-[13px] text-user-text font-semibold">
                                  string (20)
                                </span>
                                <span className="ms-2 h-[26px] rounded-full px-[14px] inline-flex justify-center items-center text-[14px] font-semibold text-user-error border border-[rgba(220,60,34,0.20)]">
                                  Required
                                </span>
                              </td>
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  A brief description of the transaction.
                                </span>
                              </td>
                            </tr>
                            <tr className="table-body-tr last:!border-0">
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  ipn_url
                                </span>
                              </td>
                              <td className="table-td table-td-2 flex items-center">
                                <span className="text-[13px] text-user-text font-semibold">
                                  string (255)
                                </span>
                                <span className="ms-2 h-[26px] rounded-full px-[14px] inline-flex justify-center items-center text-[14px] font-semibold text-user-success border border-[rgba(20,174,111,0.20)]">
                                  Optional
                                </span>
                              </td>
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  Send the notification URL where you want to
                                  receive the notification.
                                </span>
                              </td>
                            </tr>
                            <tr className="table-body-tr last:!border-0">
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  callback_url
                                </span>
                              </td>
                              <td className="table-td table-td-2 flex items-center">
                                <span className="text-[13px] text-user-text font-semibold">
                                  string (255)
                                </span>
                                <span className="ms-2 h-[26px] rounded-full px-[14px] inline-flex justify-center items-center text-[14px] font-semibold text-user-success border border-[rgba(20,174,111,0.20)]">
                                  Optional
                                </span>
                              </td>
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  Redirecting the user to the specified URL
                                  after the transaction is completed.
                                </span>
                              </td>
                            </tr>
                            <tr className="table-body-tr last:!border-0">
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  customer_name
                                </span>
                              </td>
                              <td className="table-td table-td-2 flex items-center">
                                <span className="text-[13px] text-user-text font-semibold">
                                  string (50)
                                </span>
                                <span className="ms-2 h-[26px] rounded-full px-[14px] inline-flex justify-center items-center text-[14px] font-semibold text-user-success border border-[rgba(20,174,111,0.20)]">
                                  Optional
                                </span>
                              </td>
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  Provide your customer name.
                                </span>
                              </td>
                            </tr>
                            <tr className="table-body-tr last:!border-0">
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  customer_email
                                </span>
                              </td>
                              <td className="table-td table-td-2 flex items-center">
                                <span className="text-[13px] text-user-text font-semibold">
                                  string (50)
                                </span>
                                <span className="ms-2 h-[26px] rounded-full px-[14px] inline-flex justify-center items-center text-[14px] font-semibold text-user-success border border-[rgba(20,174,111,0.20)]">
                                  Optional
                                </span>
                              </td>
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  Provide your customer email.
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-7.5 mt-7.5">
                  <CodeBlock
                    defaultLang="php"
                    dropdown={true}
                    title="Example PHP code"
                    codes={{
                      php: phpCode4,
                      java: javaCode4,
                    }}
                  />
                  <CodeBlock
                    defaultLang="php"
                    dropdown={false}
                    title="Example Response"
                    status="success"
                    statusText="Success 200"
                    codes={{
                      php: phpCode5,
                      java: javaCode5,
                    }}
                  />
                  <CodeBlock
                    defaultLang="php"
                    dropdown={false}
                    title="Example Response"
                    status="error"
                    statusText="Error (400/401)"
                    codes={{
                      php: phpCode6,
                      java: javaCode6,
                    }}
                  />
                </div>
              </div>

              <div id="ipn" className="mt-10">
                <div className="flex items-center gap-2.5 mb-4">
                  <Image
                    src={dotImage}
                    alt="dot"
                    width={100}
                    height={100}
                    className="w-[10px] h-[10px]"
                  />
                  <h5 className="text-[18px] sm:text-[20px] font-bold text-user-text">
                    IPN{" "}
                    <span className="ms-4 h-[30px] bg-[rgba(116,69,255,0.04)] rounded-[10px] px-[20px] inline-flex justify-center items-center text-[14px] font-semibold text-user-primary border border-[rgba(116,69,255,0.30)]">
                      POST
                    </span>
                  </h5>
                </div>
                <p>
                  An IPN is a POST request sent to your business application URL
                  to notify about a payment status update. It provides real-time
                  data for identifying and verifying transactions. The key
                  parameters include:
                </p>
                <ul>
                  <li className="flex flex-wrap items-center">
                    <span>URL:</span>
                    <i>Your IPN URL.</i>
                  </li>
                  <li className="flex flex-wrap items-center">
                    <span>Method:</span>
                    <i>GET</i>
                  </li>
                </ul>
                <p>You will get following parameters below.</p>
                <div>
                  <div className="max-w-full overflow-x-auto">
                    <div className="common-table common-table-2 common-table-3 min-w-[800px] border border-[rgba(45,45,45,0.10)] rounded-[10px]">
                      <div className="full-table">
                        <table className="w-full">
                          <thead className="table-head">
                            <tr className="table-head-tr">
                              <th className="table-th table-th-first-home">
                                Param Name
                              </th>
                              <th className="table-th table-th-last-home">
                                Description
                              </th>
                            </tr>
                          </thead>

                          <tbody className="table-body">
                            <tr className="table-body-tr last:!border-0">
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  status
                                </span>
                              </td>
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  Payment success status.
                                </span>
                              </td>
                            </tr>
                            <tr className="table-body-tr last:!border-0">
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  signature
                                </span>
                              </td>
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  At your end, a hash signature is used to
                                  verify the payment.
                                </span>
                              </td>
                            </tr>
                            <tr className="table-body-tr last:!border-0">
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  data
                                </span>
                              </td>
                              <td className="table-td table-td-2">
                                <span className="text-[13px] text-user-text font-semibold">
                                  The data includes charges, amount, currency,
                                  payment transaction ID, and other relevant
                                  details.
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-7.5">
                  <CodeBlock
                    defaultLang="php"
                    dropdown={true}
                    title="Example code"
                    codes={{
                      php: phpCode7,
                      java: javaCode7,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-3">
            <div className="sticky top-[150px]">
              <ul className="border-l-2 border-dashed border-[rgba(116,69,255,0.16)]">
                {navigationItems.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className={`block py-2 px-6 text-sm transition-colors duration-200 border-l-2 -ms-[2px] ${
                        activeSection === item.id
                          ? "border-user-primary text-user-primary font-semibold"
                          : "border-transparent text-user-text"
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(item.id)?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiDocumentationClient;
